import logger from '../lib/logger';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { tokenStore } from '../lib/tokenStore';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => tokenStore.get());
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.role === 'admin';

  const isSubscriptionActive = useCallback(() => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!user.subscriptionEnd) return false;
    return new Date() <= new Date(user.subscriptionEnd);
  }, [user]);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
        } else {
          tokenStore.clear();
          setToken(null);
          setUser(null);
        }
      } catch {
        tokenStore.clear();
        setToken(null);
        setUser(null);
      }
      setIsLoading(false);
    };
    validateToken();
  }, [token]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    // Read response text first, then parse
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      logger.error('JSON parse error:', e, 'Response text:', text);
      throw new Error('Login failed - server error');
    }
    
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    
    tokenStore.set(data.token);
    setToken(data.token);
    setUser({
      userId: data.userId,
      email: data.email,
      name: data.name,
      role: data.role,
      serial: data.serial,
      subscriptionStart: data.subscriptionStart,
      subscriptionEnd: data.subscriptionEnd
    });
    return data;
  };

  const signup = async (payload) => {
    const res = await fetch(`${API}/api/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Signup failed');
    tokenStore.set(data.token);
    setToken(data.token);
    setUser({
      userId: data.userId,
      email: data.email,
      name: data.name,
      role: data.role,
      serial: data.serial,
      subscriptionStart: data.subscriptionStart,
      subscriptionEnd: data.subscriptionEnd
    });
    return data;
  };

  const logout = useCallback(() => {
    tokenStore.clear();
    setToken(null);
    setUser(null);
  }, [setToken, setUser]);

  // ─── Active-Usage Heartbeat ───────────────────────────────────────────
  // Keeps a per-user `total_usage_seconds` counter on the server up to date
  // without flooding the network. One POST every 60 s while the user is
  // actively using the app:
  //   - paused when the tab is hidden
  //   - paused after IDLE_MS of no input (mouse / key / touch / scroll)
  //   - sends a final keep-alive beat on tab close so the last sub-minute
  //     window is not lost.
  // Idempotent on token change — interval restarts cleanly.
  const lastActivityRef = useRef(Date.now());
  const lastBeatRef = useRef(Date.now());
  useEffect(() => {
    if (!token || !user) return undefined;
    const TICK_MS = 60_000;       // 60 s heartbeat
    const IDLE_MS = 5 * 60_000;   // 5 min of no input → pause counter
    const MAX_DELTA_S = 120;      // server clamps anyway, mirror it client-side

    lastActivityRef.current = Date.now();
    lastBeatRef.current = Date.now();

    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }));

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Treat the moment of return as fresh — don't credit hidden time.
        lastBeatRef.current = Date.now();
        lastActivityRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const sendBeat = (deltaSeconds, useKeepAlive = false) => {
      const seconds = Math.max(0, Math.min(MAX_DELTA_S, Math.round(deltaSeconds)));
      if (seconds <= 0) return;
      // Infer per-app bucket from the current URL so the server can split
      // total_usage_seconds into Simulation / Pattern & Blend / AV Designer
      // for the admin panel. Anything else (homepage, settings, admin) →
      // "simulation" so we never lose visibility on the legacy bucket.
      const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
      let app = 'simulation';
      if (path.startsWith('/tools/test-pattern')) app = 'tpg';
      else if (path.startsWith('/tools/av-designer')) app = 'avd';
      try {
        fetch(`${API}/api/auth/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ seconds, app }),
          keepalive: useKeepAlive, // survives page unload
        }).catch(() => { /* network blip — drop, will catch up next tick */ });
      } catch (e) { /* noop */ }
    };

    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastActivityRef.current > IDLE_MS) {
        // User idle — reset baseline so we don't credit idle time on next
        // resume, but skip sending a beat.
        lastBeatRef.current = now;
        return;
      }
      const deltaS = (now - lastBeatRef.current) / 1000;
      lastBeatRef.current = now;
      sendBeat(deltaS, false);
    }, TICK_MS);

    const onUnload = () => {
      const now = Date.now();
      // Only credit if user was active recently — avoid counting a stale tab.
      if (now - lastActivityRef.current > IDLE_MS) return;
      const deltaS = (now - lastBeatRef.current) / 1000;
      sendBeat(deltaS, true);
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);

    return () => {
      clearInterval(interval);
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, [token, user]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      token,
      isAuthenticated,
      isAdmin,
      isLoading,
      isSubscriptionActive,
      login,
      signup,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};
